// React hooks
import { forwardRef, memo, useCallback, useEffect, useImperativeHandle, useState } from "react";

// Helpers i API servisi
import { useTranslate } from "helpers";
import { DataWithPagination } from "services/api";
import { deleteFleetDocumentAPI, getFleetDocumentsAPI } from "services/api/fleet/vehicles/documents/api";
import {
  FleetDocument,
  FleetDocumentRow,
} from "services/api/fleet/vehicles/documents/types";
import { initialDataWithPagination } from "services/api/consts";
import { Pagination } from "services/search";

// Tipovi i konstante
import { VehicleDocumentCardProps, VehicleDocumentRef } from "./types";

// UI komponente
import { NoResults } from "ui/components/NoResults";
import { Modal } from "ui/components/Modal";

// Moduli specifični za projekt


// Lokalne komponente
import VehicleDocumentSearchResults from "./components/VehicleDocumentSearchResults/VehicleDocumentSearchResults";
import ModalBody from "ui/components/Modal/ModalBody";
import ModalHeader from "ui/components/Modal/ModalHeader";
import {
  FLEET_VEHICLE_DOCUMENT_CREATE_PAGE_TITLE,
  FLEET_VEHICLE_DOCUMENT_EDIT_PAGE_TITLE,
} from "ui/components/PageTitle/consts";
import VehicleDocumentEditCard from "../VehicleDocumentEditCard/VehicleDocumentEditCard";
import VehicleDocumentAddCard from "ui/modules/fleet/vehicles/pages/VehicleDetailPage/components/VehicleDetailCard/components/VehicleDocumentAddCard/VehicleDocumentAddCard";
import { Dialog } from "ui/components/Dialog";

// const VehicleDocumentCard: React.FC<VehicleDocumentCardProps> = (props) => {
const VehicleDocumentCard = forwardRef<
  VehicleDocumentRef,
  VehicleDocumentCardProps
>((props, ref) => {

  const { fleetVehicleDetail } = props;

  const { t } = useTranslate();
  const [selectedDocument, setSelectedDocument] =
    useState<FleetDocument | null>(null);

  const [searchResults, setSearchResults] = useState<
    DataWithPagination<FleetDocumentRow>
  >(initialDataWithPagination);

  const [documentId, setDocumentId] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showAddNewModal, setShowAddNewModal] = useState<boolean>(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState<boolean>(false);

  useEffect(() => {
    if (fleetVehicleDetail.id)
      fetchSearchResult(searchResults.pagination, fleetVehicleDetail.id);
  }, [fleetVehicleDetail.id]);

  const fetchSearchResult = async (
    pagination: Pagination,
    vehicleId: string,
    name?: string
  ) => {
    setIsLoading(true);
    try {
      const results = await getFleetDocumentsAPI(
        pagination,
        name,
        vehicleId,
        "Vehicle"
      );

      setSearchResults(results);
    } catch {
      setSearchResults(initialDataWithPagination);
      setIsLoading(false);
      return;
    }
    setIsLoading(false);
  };

  const handleRowClick = useCallback(async (id: string) => {
    setDocumentId(id);
    setShowAddNewModal(true);
  }, []);

  // const handleAddNew = useCallback(
  //   (event: React.MouseEvent<HTMLButtonElement>) => {
  //     setDocumentId("");
  //     setShowAddNewModal(true);
  //   },
  //   []
  // );

  // Novi dokumenti se unose kao korak "Dokumenti" u čarobnjaku vozila, postojeći se uređuje u edit cardu.
  const openAddNew = () => {
    setShowUploadModal(true);
  };

  // Dokumenti se spremaju odmah pri dodavanju, pa se na zatvaranju osvježava popis.
  const handleUploadModalClose = useCallback(() => {
    setShowUploadModal(false);
    if (fleetVehicleDetail.id)
      fetchSearchResult(searchResults.pagination, fleetVehicleDetail.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fleetVehicleDetail.id, searchResults.pagination]);

  const handleEditSuccess = useCallback(() => {
    setShowAddNewModal(false);

    if (fleetVehicleDetail.id)
      fetchSearchResult(searchResults.pagination, fleetVehicleDetail.id);
  }, [fetchSearchResult]);

  const handleDeleteClick = (event: React.MouseEvent<HTMLSpanElement, MouseEvent>, id: string) => {
    event.stopPropagation();
    event.preventDefault();

    setDocumentId(id);
    setOpenDialog(true);
  }

  const handleDocumentDeleteDialogConfirm = useCallback(async () => {
    if (!documentId) return;

    try {
      const deleted = await deleteFleetDocumentAPI(
        documentId,
        t
      );

      if (deleted && fleetVehicleDetail.id) {
        await fetchSearchResult(searchResults.pagination, fleetVehicleDetail.id);
      }

      // zatvori dijalog nakon brisanja
      setOpenDialog(false);
      setDocumentId("");
    } catch (err) {
      console.error("Greška prilikom brisanja vozača:", err);
    }
  }, [documentId, t, fleetVehicleDetail.id, searchResults.pagination]);

  const handleDocumentDeleteDialogClose = useCallback(() => {
    setDocumentId("");
    setOpenDialog(false);
  }, []);

  useImperativeHandle(ref, () => ({
    openAddNew,
  }));

  if (!fleetVehicleDetail.id) return <></>;

  return (
    <div className="h-full">

      <div className="m-[-1px]">
        {!isLoading && searchResults.pagination.totalPages === 0 ? (
          <NoResults
            message={t("admin.fleet.vehicle.document.search.noresults")}
          />
        ) : (
          <VehicleDocumentSearchResults
            searchResults={searchResults}
            selectedRows={[]}
            isLoading={isLoading}
            onRowClick={handleRowClick}
            onDeleteClick={handleDeleteClick}
          />
        )}
      </div>
      <VehicleDocumentAddCard
        show={showUploadModal}
        vehicleId={fleetVehicleDetail.id}
        onClose={handleUploadModalClose}
      />
      <Modal
        show={showAddNewModal}
        onHide={() => setShowAddNewModal(false)}
        position="centered"
        size="small"
      >
        <ModalHeader
          icon="/media/images/add-icon.svg"
          title={
            documentId
              ? FLEET_VEHICLE_DOCUMENT_EDIT_PAGE_TITLE.text
              : FLEET_VEHICLE_DOCUMENT_CREATE_PAGE_TITLE.text
          }
          onHide={() => setShowAddNewModal(false)}
        />
        <ModalBody>
          {showAddNewModal && (
            <VehicleDocumentEditCard
              vehicleId={fleetVehicleDetail.id}
              documentId={documentId}
              onSuccess={handleEditSuccess}
              onClose={() => setShowAddNewModal(false)}
            />
          )}
        </ModalBody>
      </Modal>
      <Dialog
        open={openDialog}
        title={t(
          "admin.fleet.vehicle.document.dialog.delete.title"
        )}
        message={t(
          "admin.fleet.vehicle.document.dialog.delete.message"
        )}
        confirmText={t(
          "admin.fleet.vehicle.document.dialog.delete.confirmtext"
        )}
        cancelText={t(
          "admin.fleet.vehicle.document.dialog.delete.canceltext"
        )}
        onConfirm={handleDocumentDeleteDialogConfirm}
        onCancel={handleDocumentDeleteDialogClose}
      />
    </div>
  );
});

export default memo(VehicleDocumentCard);
